import { Controller } from "@hotwired/stimulus";

// 파트너 선택 모달 컨트롤러 (검색 기능 포함)
export default class extends Controller {
  static targets = [
    "modal",
    "input",
    "selectedName",
    "searchInput",
    "list",
    "hoursFilter",
  ];
  static values = {
    partners: Array,
    allowBlank: { type: Boolean, default: false },
    blankLabel: { type: String, default: "전체 파트너" },
    disabledIds: { type: Array, default: [] },
    disabledReason: { type: String, default: "선택 불가" },
    autoSubmit: { type: Boolean, default: false },
  };

  connect() {
    this.updateDisplay();
    this.minHours = 0;
  }

  openModal(event) {
    event.preventDefault();
    this.renderList();
    this.modalTarget.showModal();
    // 검색 입력 필드에 포커스
    setTimeout(() => {
      if (this.hasSearchInputTarget) {
        this.searchInputTarget.focus();
      }
    }, 100);
  }

  closeModal() {
    this.modalTarget.close();
  }

  search(event) {
    const query = event.target.value.toLowerCase().trim();
    this.renderList(query);
  }

  filterList(event) {
    const query = event.target.value.toLowerCase().trim();
    this.renderList(query);
  }

  filterByHours(event) {
    this.minHours = parseInt(event.target.value) || 0;
    const query = this.hasSearchInputTarget
      ? this.searchInputTarget.value.toLowerCase().trim()
      : "";
    this.renderList(query);
  }

  renderList(query = "") {
    const partners = this.partnersValue;
    let filteredPartners = query
      ? partners.filter((p) => p.name.toLowerCase().includes(query))
      : partners;

    // 타임빌딩 시간 필터 적용
    if (this.minHours > 0) {
      filteredPartners = filteredPartners.filter(
        (p) => (p.total_work_hours || 0) >= this.minHours,
      );
    }

    if (filteredPartners.length === 0) {
      this.listTarget.innerHTML = `
        <div class="text-center py-8 text-gray-500">
          <p>검색 결과가 없습니다</p>
        </div>
      `;
      return;
    }

    const currentValue = this.inputTarget.value;

    // "전체" 옵션 (allowBlank이 true이고 검색어가 없을 때만 표시)
    let blankOption = "";
    if (this.allowBlankValue && !query) {
      const isSelected = !currentValue || currentValue === "";
      blankOption = `
        <button type="button"
                class="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors ${isSelected ? "bg-blue-50" : ""}"
                data-action="click->partner-select-modal#selectBlank">
          <div class="text-left">
            <p class="font-medium text-gray-900">${this.blankLabelValue}</p>
          </div>
          ${
            isSelected
              ? `
            <svg class="w-5 h-5 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
            </svg>
          `
              : ""
          }
        </button>
      `;
    }

    const disabledIds = this.disabledIdsValue.map((id) => id.toString());
    const disabledReason = this.disabledReasonValue;

    this.listTarget.innerHTML =
      blankOption +
      filteredPartners
        .map((partner) => {
          const isDisabled = disabledIds.includes(partner.id.toString());
          const isSelected = partner.id.toString() === currentValue;

          const workHours = partner.total_work_hours || 0;
          const hoursDisplay =
            workHours > 0
              ? `<span class="text-xs text-blue-500 ml-2">${workHours}시간</span>`
              : "";

          if (isDisabled) {
            return `
          <div class="w-full flex items-center justify-between px-4 py-3 bg-gray-50 cursor-not-allowed opacity-60">
            <div class="text-left">
              <p class="font-medium text-gray-400">${partner.name}${hoursDisplay}</p>
              <p class="text-sm text-red-400">${disabledReason}</p>
            </div>
            <svg class="w-5 h-5 text-gray-300 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"/>
            </svg>
          </div>
        `;
          }

          return `
        <button type="button"
                class="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors ${isSelected ? "bg-blue-50" : ""}"
                data-action="click->partner-select-modal#selectPartner"
                data-id="${partner.id}"
                data-name="${partner.name}">
          <div class="text-left">
            <p class="font-medium text-gray-900">${partner.name}${hoursDisplay}</p>
            ${partner.phone ? `<p class="text-sm text-gray-500">${partner.phone}</p>` : ""}
          </div>
          ${
            isSelected
              ? `
            <svg class="w-5 h-5 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
            </svg>
          `
              : ""
          }
        </button>
      `;
        })
        .join("");
  }

  selectBlank() {
    this.inputTarget.value = "";
    this.selectedNameTarget.textContent = this.blankLabelValue;
    this.selectedNameTarget.classList.add("text-gray-400");
    this.selectedNameTarget.classList.remove("text-gray-900");

    this.closeModal();
    this.#afterSelect();
  }

  selectPartner(event) {
    const id = event.currentTarget.dataset.id;
    const name = event.currentTarget.dataset.name;

    this.inputTarget.value = id;
    this.selectedNameTarget.textContent = name;
    this.selectedNameTarget.classList.remove("text-gray-400");
    this.selectedNameTarget.classList.add("text-gray-900");

    this.closeModal();
    this.#afterSelect();
  }

  #afterSelect() {
    if (this.autoSubmitValue) {
      const form = this.inputTarget.closest("form");
      if (form) form.requestSubmit();
    } else {
      this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }

  updateDisplay() {
    const currentValue = this.inputTarget.value;
    if (currentValue) {
      const partner = this.partnersValue.find(
        (p) => p.id.toString() === currentValue,
      );
      if (partner && this.hasSelectedNameTarget) {
        this.selectedNameTarget.textContent = partner.name;
        this.selectedNameTarget.classList.remove("text-gray-400");
        this.selectedNameTarget.classList.add("text-gray-900");
      }
    }
  }

  // ESC 키로 모달 닫기
  handleKeydown(event) {
    if (event.key === "Escape") {
      this.closeModal();
    }
  }

  // 모달 외부 클릭 시 닫기
  handleBackdropClick(event) {
    if (event.target === this.modalTarget) {
      this.closeModal();
    }
  }
}
